// ============================================================================
// Custom Hook: useEventBus - Subscribe to events (MEMORY LEAK SAFE)
// ============================================================================
import { useEffect, useRef } from "react";

import { type EventBusEventsType, eventBus } from "./event-bus";

/**
 * Subscribe to an event bus event with automatic cleanup
 * Handler is always stable - no need to worry about dependencies
 */
export function useEventBus<E extends keyof EventBusEventsType>(
  event: E,
  handler: (payload: EventBusEventsType[E]) => void
) {
  const handlerRef = useRef(handler);

  // Always keep the handler ref up to date
  // This runs after every render, so handler is never stale
  useEffect(() => {
    handlerRef.current = handler;
  });

  // Subscribe to the event (only when event changes)
  useEffect(() => {
    const wrappedHandler = (payload: EventBusEventsType[E]) => {
      handlerRef.current(payload);
    };

    const unsubscribe = eventBus.on(event, wrappedHandler);

    // Cleanup: unsubscribe when component unmounts or event changes
    return () => {
      unsubscribe();
    };
  }, [event]); // Only re-subscribe if event name changes
}
